<app-page [showBackButton]="true" title="New System Approval">
  <form (ngSubmit)="createSystem()" [formGroup]="systemForm" class="grid p-fluid w-full">
    <!-- Actions Bar -->
    <div class="col-12">
      <div class="flex justify-content-between align-items-center pb-2 mb-3 border-bottom-1 border-300">
        <h2 class="m-0 text-lg font-semibold">System Information</h2>
        <div class="flex gap-2">
          <button
            pButton
            type="submit"
            class="p-button-sm"
            [disabled]="systemForm.invalid || systemNumberValid() === false"
            data-testid="create-system-button"><i class="pi pi-save" pButtonIcon></i><span pButtonLabel>Save</span>
          </button>
        </div>
      </div>
    </div>

    <!-- System Type Selection -->
    <div class="field mb-2 col-12 md:col-6">
      <app-select
        [label]="'System Type'"
        [options]="systemTypeOptions"
        [required]="true"
        id="systemType"
        testId="systemType"
      ></app-select>
    </div>

    <!-- System Number (editable) - hidden for class-3 coating systems which defer number assignment -->
    @if (!isClass3Coating()) {
      <div class="field mb-2 col-12 md:col-6">
        <label for="systemNumber" class="font-medium text-900">
          System Number <span class="text-red-500">*</span>
        </label>
        <p-inputgroup>
          <p-inputgroup-addon>{{ formattedSystemNumber().split('-')[0] || '?' }}-</p-inputgroup-addon>
          <input
            pInputText
            type="number"
            formControlName="systemNumber"
            id="systemNumber"
            min="1"
            (blur)="onSystemNumberBlur()"
            class="flex-1"
            data-testid="systemNumber" />
          @if (checkingNumber()) {
            <p-inputgroup-addon>
              <i class="pi pi-spin pi-spinner"></i>
            </p-inputgroup-addon>
          } @else if (systemNumberValid() === true) {
            <p-inputgroup-addon class="text-green-500">
              <i class="pi pi-check"></i>
            </p-inputgroup-addon>
          } @else if (systemNumberValid() === false) {
            <p-inputgroup-addon class="text-red-500">
              <i class="pi pi-times"></i>
            </p-inputgroup-addon>
          }
        </p-inputgroup>
        @if (systemNumberError()) {
          <small class="p-error">{{ systemNumberError() }}</small>
        }
      </div>
    } @else {
      <div class="field mb-2 col-12 md:col-6">
        <label class="font-medium text-900">System Number</label>
        <p-message severity="info">Class 3 systems start in Granting — a P-number is assigned when the system is granted to Provisional.</p-message>
      </div>
    }

    <!-- Supplier Selection -->
    <div class="field mb-2 col-12 md:col-6">
      <app-autocomplete
        (itemSelected)="supplierSelected($event)"
        (searchQueryChanged)="searchSupplier($event)"
        [optionLabel]="'name'"
        [required]="true"
        [suggestions]="organisations()"
        [readonly]="!systemForm.get('systemType')?.value"
        id="supplier"
        label="Supplier / Manufacturer"
        testId="supplier"
      ></app-autocomplete>
      @if (!systemForm.get('systemType')?.value) {
        <small class="text-500">Select a system type first</small>
      }
    </div>

    <!-- Production Site Selection -->
    @if (hasSelectedSupplier()) {
      @if (showProductionSiteSelector()) {
        <div class="field mb-2 col-12 md:col-6">
          <app-select
            [label]="'Production Site'"
            [options]="productionSiteOptions()"
            [required]="true"
            id="productionSiteId"
            testId="productionSite"
          ></app-select>
        </div>
      } @else if (productionSiteOptions().length === 1) {
        <div class="field mb-2 col-12 md:col-6">
          <label class="font-medium text-900">Production Site</label>
          <div class="production-site-display p-2 border-round surface-100">
            {{ productionSiteOptions()[0]?.label }}
          </div>
        </div>
      }
    }

    <!-- System Name -->
    <div class="field mb-2 col-12 md:col-6">
      <label for="systemName" class="font-medium text-900">
        System Name <span class="text-red-500">*</span>
      </label>
      <input
        pInputText
        formControlName="systemName"
        id="systemName"
        class="w-full"
        [maxlength]="100"
        data-testid="systemName" />
      @if (systemForm.get('systemName')?.hasError('maxlength')) {
        <small class="p-error">System name must not exceed 100 characters</small>
      }
    </div>

    <!-- Date of Granting - hidden for class-3 coating systems -->
    @if (!isClass3Coating()) {
      <div class="field mb-2 col-12 md:col-6">
        <app-calendar
          label="Date of Granting"
          id="approvalDate"
          [required]="true"
          testId="approvalDate"
        ></app-calendar>
      </div>
    }

    <!-- Coating System-specific fields -->
    @if (isCoatingSystem()) {
      <div class="col-12">
        <div class="pb-2 mb-3 border-bottom-1 border-300">
          <h3 class="m-0 text-base font-semibold">Coating System Details (Optional)</h3>
        </div>
      </div>

      <!-- P-Code -->
      <div class="field mb-2 col-12 md:col-6">
        <label for="pCode" class="font-medium text-900">P-Code</label>
        <input
          pInputText
          formControlName="pCode"
          id="pCode"
          class="w-full"
          [maxlength]="10"
          data-testid="pCode" />
      </div>

      <!-- P-Class -->
      <div class="field mb-2 col-12 md:col-6">
        <app-select
          [label]="'Class'"
          [options]="pClassOptions"
          [required]="false"
          id="pClass"
          testId="pClass"
        ></app-select>
      </div>

      <!-- Gloss Category -->
      <div class="field mb-2 col-12 md:col-6">
        <app-select
          [label]="'Gloss Category'"
          [options]="glossCategoryOptions"
          [required]="false"
          id="glossCategory"
          testId="glossCategory"
        ></app-select>
      </div>

      <!-- Structure Finish -->
      <div class="field mb-2 col-12 md:col-6">
        <app-select
          [label]="'Structure Finish'"
          [options]="structureFinishOptions"
          [required]="false"
          id="structureFinish"
          testId="structureFinish"
        ></app-select>
      </div>

      <!-- Coating Material -->
      <div class="field mb-2 col-12 md:col-6">
        <app-select
          [label]="'Coating Material'"
          [options]="coatingMaterialOptions"
          [required]="false"
          id="coatingMaterial"
          testId="coatingMaterial"
        ></app-select>
      </div>

      <!-- Approval Type -->
      <div class="field mb-2 col-12 md:col-6">
        <app-select
          [label]="'Approval Type'"
          [options]="approvalTypeOptions"
          [required]="false"
          id="approvalType"
          testId="approvalType"
        ></app-select>
      </div>

      <!-- Metallic Colours Approved -->
      <div class="field mb-2 col-12 md:col-6 flex align-items-end">
        <div class="flex align-items-center gap-2 pb-2">
          <p-checkbox
            formControlName="metallicColoursApproved"
            [binary]="true"
            inputId="metallicColoursApproved"
            data-testid="metallicColoursApproved">
          </p-checkbox>
          <label for="metallicColoursApproved" class="font-medium text-900 cursor-pointer">
            Metallic Colours Approved
          </label>
        </div>
      </div>
    }

    <!-- Pretreatment-specific fields -->
    @if (isPretreatmentSystem()) {
      <div class="col-12">
        <div class="pb-2 mb-3 border-bottom-1 border-300">
          <h3 class="m-0 text-base font-semibold">Pretreatment System Details (Optional)</h3>
        </div>
      </div>

      <!-- Outdoor Exposure Test Start Date -->
      <div class="field mb-2 col-12 md:col-6">
        <app-calendar
          label="Outdoor Exposure Test Start Date"
          id="outdoorExposureTestStartDate"
          [required]="false"
          testId="outdoorExposureTestStartDate"
        ></app-calendar>
      </div>

      <!-- Rinse Process -->
      <div class="field mb-2 col-12 md:col-6">
        <label for="rinseProcess" class="font-medium text-900">Rinse Process</label>
        <input
          pInputText
          formControlName="rinseProcess"
          id="rinseProcess"
          class="w-full"
          [maxlength]="100"
          data-testid="rinseProcess" />
      </div>
    }
  </form>
</app-page>
